import React, { useState, useEffect, useCallback } from 'react';
import { Send, ExternalLink, RefreshCw } from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { Pagination } from '../components/Pagination';

const LIMIT = 30;

function StatusBadge({ share }) {
  if (share.replied) return <span className="admin-badge admin-badge-success">Replied</span>;
  if (share.open_count > 0) return <span className="admin-badge admin-badge-info">Opened</span>;
  return <span className="admin-badge">Sent</span>;
}

export function SharesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getQaShares({ skip: (page - 1) * LIMIT, limit: LIMIT });
      setItems(data.items);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to load shares:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Shares</h1>
        <button className="admin-btn admin-btn-secondary" onClick={load} title="Refresh">
          <RefreshCw size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Refresh
        </button>
      </div>

      <div className="admin-card" style={{ padding: 0 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Owner</th>
              <th>Recipient</th>
              <th>Findings</th>
              <th>Opens</th>
              <th>Status</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40 }}>Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                  <Send size={20} style={{ opacity: 0.4, marginBottom: 8 }} />
                  <div>No shared reports yet - nobody has shared a QA report with a client.</div>
                </td>
              </tr>
            ) : (
              items.map((s) => (
                <tr key={s.token}>
                  <td style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {new Date(s.created_at).toLocaleString()}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {s.owner_email ? (
                      <a href={`mailto:${s.owner_email}`} style={{ color: 'var(--brand-500)', textDecoration: 'none' }}>
                        {s.owner_email}
                      </a>
                    ) : (
                      <span style={{ color: 'var(--text-disabled)' }}>deleted user</span>
                    )}
                  </td>
                  <td style={{ fontSize: 13 }}>{s.recipient || <span style={{ color: 'var(--text-disabled)' }}>-</span>}</td>
                  <td style={{ fontSize: 13 }}>{s.findings_count}</td>
                  <td style={{ fontSize: 13 }}>{s.open_count}</td>
                  <td><StatusBadge share={s} /></td>
                  <td>
                    <a
                      href={s.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--text-muted)' }}
                      title="Open the public report"
                    >
                      <ExternalLink size={16} />
                    </a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
}
